import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { Pericia, PericiaPayload, Quesito, QuesitoPayload, Profissional, ProfissionalPayload, VinculoProfissional, VinculoProfissionalPayload, TarefaPericia, TarefaPericiaPayload, PlanejamentoPericia, PlanejamentoPayload, ProgramaTrabalho, ProgramaTrabalhoPayload, DocumentoPericia, DocumentoPayload, DiligenciaPericia, DiligenciaPayload, HonorarioEstimativa, HonorarioEstimativaPayload, RetornoNomeacao, RetornoNomeacaoPayload } from '../models/pericia.model';

export interface VariavelConfig {
  id?: number;
  nome: string;
  chave: string;
  valor: string;
  descricao: string;
  created_at?: string;
}

export interface ConfiguracaoApp {
  diretorio_retorno: string;
  observacoes: string;
  variaveis: VariavelConfig[];
}

@Injectable({ providedIn: 'root' })
export class PericiasService {
  private readonly http = inject(HttpClient);
  private readonly apiUrl = '/api';

  listar(): Observable<Pericia[]> {
    return this.http.get<Pericia[]>(`${this.apiUrl}/pericias`);
  }

  obter(id: number): Observable<Pericia> {
    return this.http.get<Pericia>(`${this.apiUrl}/pericias/${id}`);
  }

  criar(payload: PericiaPayload): Observable<Pericia> {
    return this.http.post<Pericia>(`${this.apiUrl}/pericias`, payload);
  }

  atualizar(id: number, payload: PericiaPayload): Observable<Pericia> {
    return this.http.put<Pericia>(`${this.apiUrl}/pericias/${id}`, payload);
  }


  listarQuesitos(periciaId: number): Observable<Quesito[]> {
    return this.http.get<Quesito[]>(`${this.apiUrl}/pericias/${periciaId}/quesitos`);
  }

  criarQuesito(periciaId: number, payload: QuesitoPayload): Observable<Quesito> {
    return this.http.post<Quesito>(`${this.apiUrl}/pericias/${periciaId}/quesitos`, payload);
  }

  atualizarQuesito(quesitoId: number, payload: QuesitoPayload): Observable<Quesito> {
    return this.http.put<Quesito>(`${this.apiUrl}/quesitos/${quesitoId}`, payload);
  }

  listarProfissionais(): Observable<Profissional[]> {
    return this.http.get<Profissional[]>(`${this.apiUrl}/profissionais`);
  }

  criarProfissional(payload: ProfissionalPayload): Observable<Profissional> {
    return this.http.post<Profissional>(`${this.apiUrl}/profissionais`, payload);
  }

  atualizarProfissional(id: number, payload: ProfissionalPayload): Observable<Profissional> {
    return this.http.put<Profissional>(`${this.apiUrl}/profissionais/${id}`, payload);
  }

  excluirProfissional(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/profissionais/${id}`);
  }

  listarVinculosProfissionais(periciaId: number): Observable<VinculoProfissional[]> {
    return this.http.get<VinculoProfissional[]>(`${this.apiUrl}/pericias/${periciaId}/profissionais`);
  }

  criarVinculoProfissional(periciaId: number, payload: VinculoProfissionalPayload): Observable<VinculoProfissional> {
    return this.http.post<VinculoProfissional>(`${this.apiUrl}/pericias/${periciaId}/profissionais`, payload);
  }

  excluirVinculoProfissional(vinculoId: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/pericias/profissionais/${vinculoId}`);
  }


  obterPlanejamento(periciaId: number): Observable<PlanejamentoPericia | null> {
    return this.http.get<PlanejamentoPericia | null>(`${this.apiUrl}/pericias/${periciaId}/planejamento`);
  }

  salvarPlanejamento(periciaId: number, payload: PlanejamentoPayload): Observable<PlanejamentoPericia> {
    return this.http.put<PlanejamentoPericia>(`${this.apiUrl}/pericias/${periciaId}/planejamento`, payload);
  }

  listarProgramasTrabalho(): Observable<ProgramaTrabalho[]> {
    return this.http.get<ProgramaTrabalho[]>(`${this.apiUrl}/programas-trabalho`);
  }

  listarProgramasTrabalhoPericia(periciaId: number): Observable<ProgramaTrabalho[]> {
    return this.http.get<ProgramaTrabalho[]>(`${this.apiUrl}/pericias/${periciaId}/programas-trabalho`);
  }

  criarProgramaTrabalho(periciaId: number, payload: ProgramaTrabalhoPayload): Observable<ProgramaTrabalho> {
    return this.http.post<ProgramaTrabalho>(`${this.apiUrl}/pericias/${periciaId}/programas-trabalho`, payload);
  }

  atualizarProgramaTrabalho(id: number, payload: ProgramaTrabalhoPayload): Observable<ProgramaTrabalho> {
    return this.http.put<ProgramaTrabalho>(`${this.apiUrl}/programas-trabalho/${id}`, payload);
  }

  excluirProgramaTrabalho(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/programas-trabalho/${id}`);
  }


  listarDocumentosPericia(periciaId: number): Observable<DocumentoPericia[]> {
    return this.http.get<DocumentoPericia[]>(`${this.apiUrl}/pericias/${periciaId}/documentos`);
  }

  criarDocumento(periciaId: number, payload: DocumentoPayload): Observable<DocumentoPericia> {
    return this.http.post<DocumentoPericia>(`${this.apiUrl}/pericias/${periciaId}/documentos`, payload);
  }

  atualizarDocumento(id: number, payload: DocumentoPayload): Observable<DocumentoPericia> {
    return this.http.put<DocumentoPericia>(`${this.apiUrl}/documentos/${id}`, payload);
  }

  excluirDocumento(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/documentos/${id}`);
  }


  uploadDocumento(periciaId: number, file: File): Observable<any> {
    const form = new FormData();
    form.append('file', file);
    return this.http.post<any>(`${this.apiUrl}/pericias/${periciaId}/documentos/upload`, form);
  }

  baixarDocumento(id: number): Observable<Blob> {
    return this.http.get(`${this.apiUrl}/documentos/${id}/download`, {
      responseType: 'blob'
    });
  }

  listarDiligenciasPericia(periciaId: number): Observable<DiligenciaPericia[]> {
    return this.http.get<DiligenciaPericia[]>(`${this.apiUrl}/pericias/${periciaId}/diligencias`);
  }

  criarDiligencia(periciaId: number, payload: DiligenciaPayload): Observable<DiligenciaPericia> {
    return this.http.post<DiligenciaPericia>(`${this.apiUrl}/pericias/${periciaId}/diligencias`, payload);
  }

  atualizarDiligencia(id: number, payload: DiligenciaPayload): Observable<DiligenciaPericia> {
    return this.http.put<DiligenciaPericia>(`${this.apiUrl}/diligencias/${id}`, payload);
  }

  excluirDiligencia(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/diligencias/${id}`);
  }

  listarTarefas(): Observable<TarefaPericia[]> {
    return this.http.get<TarefaPericia[]>(`${this.apiUrl}/tarefas`);
  }

  listarTarefasPericia(periciaId: number): Observable<TarefaPericia[]> {
    return this.http.get<TarefaPericia[]>(`${this.apiUrl}/pericias/${periciaId}/tarefas`);
  }

  criarTarefa(periciaId: number, payload: TarefaPericiaPayload): Observable<TarefaPericia> {
    return this.http.post<TarefaPericia>(`${this.apiUrl}/pericias/${periciaId}/tarefas`, payload);
  }

  atualizarTarefa(id: number, payload: TarefaPericiaPayload): Observable<TarefaPericia> {
    return this.http.put<TarefaPericia>(`${this.apiUrl}/tarefas/${id}`, payload);
  }

  excluirTarefa(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/tarefas/${id}`);
  }


  excluirQuesito(quesitoId: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/quesitos/${quesitoId}`);
  }



  listarVariaveis(): Observable<VariavelConfig[]> {
    return this.http.get<VariavelConfig[]>(`${this.apiUrl}/variaveis`);
  }

  criarVariavel(payload: VariavelConfig): Observable<VariavelConfig[]> {
    return this.http.post<VariavelConfig[]>(`${this.apiUrl}/variaveis`, payload);
  }

  atualizarVariavel(chave: string, payload: VariavelConfig): Observable<VariavelConfig[]> {
    return this.http.post<VariavelConfig[]>(`${this.apiUrl}/variaveis`, payload);
  }

  excluirVariavel(chave: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/variaveis/${encodeURIComponent(chave)}`);
  }

  obterConfiguracoes(): Observable<ConfiguracaoApp> {
    return this.http.get<ConfiguracaoApp>(`${this.apiUrl}/configuracoes`);
  }

  salvarConfiguracoes(payload: ConfiguracaoApp): Observable<ConfiguracaoApp> {
    return this.http.post<ConfiguracaoApp>(`${this.apiUrl}/configuracoes/salvar`, payload);
  }


  obterHonorarios(periciaId: number): Observable<HonorarioEstimativa | null> {
    return this.http.get<HonorarioEstimativa | null>(`${this.apiUrl}/pericias/${periciaId}/honorarios`);
  }

  salvarHonorarios(periciaId: number, payload: HonorarioEstimativaPayload): Observable<HonorarioEstimativa> {
    return this.http.put<HonorarioEstimativa>(`${this.apiUrl}/pericias/${periciaId}/honorarios`, payload);
  }

  previewHonorariosHtml(periciaId: number, destino: string): Observable<string> {
    const url = `${this.apiUrl}/pericias/${periciaId}/honorarios/preview?destino=${encodeURIComponent(destino)}`;
    return this.http.get(url, { responseType: 'text' });
  }

  exportarHonorariosHtml(periciaId: number, destino: string): void {
    const url = `${this.apiUrl}/pericias/${periciaId}/honorarios/exportar-html?destino=${encodeURIComponent(destino)}`;
    window.open(url, '_blank');
  }

  exportarHonorariosArquivo(periciaId: number, destino: string, formato: string): void {
    const url = `${this.apiUrl}/pericias/${periciaId}/honorarios/exportar?destino=${encodeURIComponent(destino)}&formato=${encodeURIComponent(formato)}`;
    window.open(url, '_blank');
  }

  obterRetornoNomeacao(periciaId: number): Observable<RetornoNomeacao | null> {
    return this.http.get<RetornoNomeacao | null>(`${this.apiUrl}/pericias/${periciaId}/retorno-nomeacao`);
  }

  salvarRetornoNomeacao(periciaId: number, payload: RetornoNomeacaoPayload): Observable<RetornoNomeacao> {
    return this.http.put<RetornoNomeacao>(`${this.apiUrl}/pericias/${periciaId}/retorno-nomeacao`, payload);
  }

  previewRetornoNomeacaoHtml(periciaId: number): Observable<string> {
    return this.http.get(`${this.apiUrl}/pericias/${periciaId}/retorno-nomeacao/preview`, { responseType: 'text' });
  }

  exportarRetornoNomeacao(periciaId: number, formato: string): void {
    const url = `${this.apiUrl}/pericias/${periciaId}/retorno-nomeacao/exportar?formato=${encodeURIComponent(formato)}`;
    window.open(url, '_blank');
  }

  excluir(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/pericias/${id}`);
  }
}
